interface AuthConfig {
  casdoor_endpoint: string;
  client_id: string;
  redirect_uri: string;
  org_name: string;
  app_name: string;
}

const STATE_STORAGE_KEY = "meteo-alerts-oauth-state";

let accessToken: string | null = null;

export function isAuthenticated(): boolean {
  return accessToken !== null;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function getAuthConfig(): Promise<AuthConfig> {
  const response = await fetch("/auth/config");
  if (!response.ok) throw new Error("Nie udało się pobrać konfiguracji logowania.");
  return (await response.json()) as AuthConfig;
}

export async function login(): Promise<void> {
  const config = await getAuthConfig();
  const state = crypto.randomUUID();
  sessionStorage.setItem(STATE_STORAGE_KEY, state);

  const url = new URL(`${config.casdoor_endpoint}/login/oauth/authorize`);
  url.searchParams.set("client_id", config.client_id);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("redirect_uri", config.redirect_uri);
  url.searchParams.set("scope", "read");
  url.searchParams.set("state", state);
  window.location.href = url.toString();
}

export async function logout(): Promise<void> {
  try {
    const response = await fetch("/auth/logout", { method: "POST", credentials: "include" });
    if (response.ok) accessToken = null;
  } catch {
    // Keep the client session as-is on a network failure, rather than showing
    // logged-out while the server-side session may still be valid.
  }
}

async function exchangeCodeForToken(code: string): Promise<void> {
  const response = await fetch("/auth/exchange-code", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ code }),
  });
  if (!response.ok) throw new Error("Nie udało się wymienić kodu logowania na token.");
  const body = (await response.json()) as { access_token: string };
  accessToken = body.access_token;
}

export async function tryRefresh(): Promise<void> {
  try {
    const response = await fetch("/auth/refresh-token", { method: "POST", credentials: "include" });
    if (!response.ok) return;
    const body = (await response.json()) as { access_token: string };
    accessToken = body.access_token;
  } catch {
    // Silent — refresh is a best-effort background attempt, a failure here just
    // leaves the caller unauthenticated instead of aborting anything.
  }
}

async function consumeRedirectCode(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  if (!code) return;

  const expectedState = sessionStorage.getItem(STATE_STORAGE_KEY);
  sessionStorage.removeItem(STATE_STORAGE_KEY);
  history.replaceState(null, "", window.location.pathname);
  if (!expectedState || state !== expectedState) return;

  await exchangeCodeForToken(code);
}

export async function ensureSession(): Promise<void> {
  await consumeRedirectCode();
  if (!isAuthenticated()) {
    await tryRefresh();
  }
}
